import React, { useState, useEffect } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { fetchInsights } from '../api';
import { useTranslation } from 'react-i18next';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL!;

interface Insight {
  _id: string;
  title: { en: string; ar: string };
  priority: number;
  isActive: boolean;
  image: string;
}

interface InsightItem {
  title: string;
  image: string;
}

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3,
    slidesToSlide: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 768 },
    items: 2,
    slidesToSlide: 1,
  },
  mobile: {
    breakpoint: { max: 768, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
};

const Insights: React.FC = () => {
  const [insights, setInsights] = useState<InsightItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  // Fetch insights on component mount
  useEffect(() => {
    const loadInsights = async () => {
      try {
        const data = await fetchInsights();
        // Filter active insights and sort by priority
        const activeInsights = data.insights
          .filter((i: Insight) => i.isActive)
          .sort((a: Insight, b: Insight) => a.priority - b.priority)
          .map((i: Insight) => ({
            title: i18n.language === 'ar' ? i.title.ar : i.title.en,
            image: `${IMAGE_BASE_URL}/${i.image}`,
          }));
        setInsights(activeInsights);
        setLoading(false);
      } catch (err) {
        setError(t('errors.fetchInsights'));
        setLoading(false);
      }
    };
    loadInsights();
  }, [t, i18n.language]);

  if (loading) {
    return <div>{t('loading')}</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <section className="insights">
      <div className="container">
        <div className="row text-center mb-4">
          <div className="col-md-12">
            <div className="content_wrapper">
              <h2>{t('insights')}</h2>
            </div>
          </div>
        </div>

        <Carousel
          responsive={responsive}
          infinite
          arrows={false}
          showDots={false}
          autoPlay={true}
          autoPlaySpeed={3000}
          containerClass="carousel-container"
          itemClass="px-3 pb-5"
        >
          {insights.map((item, index) => (
            <div
              className="box_wraper position-relative pt-4 text-center"
              key={index}
              style={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}
            >
              <img
                src={item.image}
                alt={item.title}
                width={300}
                height={200}
                className="img-fluid"
              />
              <div className="caption_details">
                <h4>
                  {item.title.length > 20
                    ? item.title.substring(0, 20) + '...'
                    : item.title}
                </h4>
              </div>
            </div>
          ))}
        </Carousel>
      </div>
      <style>{`
        .caption_details h4 {
          direction: ${i18n.language === 'ar' ? 'rtl' : 'ltr'};
          text-align: ${i18n.language === 'ar' ? 'right' : 'center'};
        }
      `}</style>
    </section>
  );
};

export default Insights;
